<template>
  <div
    ref="facets"
    v-on-click-outside="toggleFacet"
    :class="{
      'facets--mobile': !desktop,
      'facets--desktop': desktop,
    }"
    class="facets"
  >
    <div
      v-if="!desktop"
      class="facets-header"
    >
      <fn-link
        arrow-left
        class="f-close"
        pre-arrow
        @click="$emit('showResults')"
      >
        {{ $t('filter.close') }}
      </fn-link>
      <fn-icon icon="filter" />
      {{ $t('filter.filters') }}
      <fn-link
        v-if="hasSelectedFacets"
        class="f-delete"
        no-arrow
        @click="deleteAllFacets(facets)"
      >
        {{ $t('filter.deleteAll') }}
      </fn-link>
    </div>
    <div class="facets-wrapper">
      <fn-facet
        v-for="(facet, index) in filteredFacets"
        v-show="isFacetVisible(index)"
        :id="`facet-${facet.name}`"
        :key="facet.code"
        ref="facet"
        :child-facets="getChildFacets(facet)"
        :desktop="desktop"
        :facet="facet"
        :is-brand-page="isBrandPage"
        :open="openFacetCode === facet.code"
        @save="(payload) => $emit('filter', payload)"
        @hide-teaser="$emit('teaser')"
        @toggle-facet="toggleFacet"
      />

      <fn-button
        v-if="isShowAllFiltersButtonVisible"
        class="facet-button facet-button--show-all facet"
        @click="showAll"
      >
        {{ $t('filter.showAllFilters') }}
      </fn-button>
      <fn-button
        v-if="isDeleteAllFiltersButtonVisible"
        class="facet-button facet-button--delete-all facet"
        @click="deleteAllFacets(facets)"
      >
        {{ $t('filter.deleteAllFilters') }}
      </fn-button>
    </div>
    <div
      v-if="!desktop"
      class="facets-footer"
    >
      <fn-button
        class="show-results"
        secondary
        @click="$emit('showResults')"
      >
        {{ $t('filter.showResults', totalResults, { resultsCount: totalResults }) }}
      </fn-button>
    </div>
  </div>
</template>

<script lang="ts">
import { vOnClickOutside } from '@vueuse/components';
import { PRICE_RANGE, BADGES_FACET, DISCOUNT } from '@/utils/facetConstants';

export default defineComponent({
  directives: {
    'on-click-outside': vOnClickOutside,
  },
  props: {
    facets: {
      type: Array,
      required: true,
    },
    desktop: {
      type: Boolean,
    },
    totalResults: {
      type: Number,
      default: 0,
    },
    hiddenFilters: {
      type: [Array, Boolean],
      default: () => [],
    },
    isBrandPage: {
      type: Boolean,
    },
    lazy: {
      type: Boolean,
    },
  },
  emits: [
    // Event emitters for component
    'showResults',
    'teaser',
    'filter',

    // Event emitters for useFacets composable
    'deleteAllFacets',
    'showResults',
  ],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setup(props, context) {
    const { deleteAllFacets } = useFacets(context);

    return {
      deleteAllFacets,
    };
  },
  data() {
    return {
      openFacetCode: null,
      showAllFilters: false,
      maxInitialVisibleFacets: 10,
      recentCode: null,

      /**
       *  @see https://confluence.fressnapf.de/pages/viewpage.action?pageId=178199559
       */
      childFacetConfig: [
        {
          parent: PRICE_RANGE,
          child: BADGES_FACET,
          facetItemWhitelist: [DISCOUNT],
        },
      ],
    };
  },
  computed: {
    filteredFacets() {
      const hiddenFacetCodes = this.hiddenFilters?.length ? this.hiddenFilters.map((filter) => filter.key) : [];

      return this.facets.filter((facet) => {
        const isHidden = hiddenFacetCodes.includes(facet.code);
        const isBrandOrNonCategoryFacet = facet.brand || !facet.category;

        return !isHidden && isBrandOrNonCategoryFacet;
      });
    },

    hasSelectedFacets() {
      let hasSelectedFacetValue = false;

      for (let i = 0; i < this.facets.length; i += 1) {
        const facet = this.facets[i];

        if (hasSelectedFacetValue) {
          break; // one selected value is all we need
        }

        for (let j = 0; j < facet.values.length; j += 1) {
          const value = facet.values[j];

          if (value.selected) {
            let initialFilters = [];

            if (this.hiddenFilters) {
              initialFilters = this.hiddenFilters?.filter((filter) => filter.value === value.code);
            }

            if (initialFilters.length <= 0) {
              hasSelectedFacetValue = true;
              break; // found the one
            }
          }
        }
      }

      return hasSelectedFacetValue;
    },

    isShowAllFiltersButtonVisible() {
      return this.desktop && !this.showAllFilters && this.filteredFacets.length > this.maxInitialVisibleFacets;
    },

    isDeleteAllFiltersButtonVisible() {
      return this.desktop && this.hasSelectedFacets;
    },
  },
  watch: {
    facets() {
      this.openFacetCode = null;
    },
  },
  methods: {
    getChildFacets(parentFacet) {
      const childFacets = [];

      for (const childFacet of this.childFacetConfig) {
        if (childFacet.parent === parentFacet.code) {
          for (const facet of this.facets) {
            if (childFacet.child === facet.code) {
              childFacets.push({
                ...facet,
                facetItemWhitelist: childFacet.facetItemWhitelist,
              });
            }
          }
        }
      }

      return childFacets;
    },

    isFacetVisible(index) {
      return !this.desktop || (this.desktop && this.showAllFilters) || index < this.maxInitialVisibleFacets;
    },

    /**
     * Toggle a facet depending on its open state.
     * If values change inside facet and click outside occurs, then the save method of
     * the particular facet is triggered.
     * @param facetCode {String|Event}
     */
    toggleFacet(facetCode) {
      this.openFacetCode = typeof facetCode === 'string' ? facetCode : null;
      this.recentCode = typeof facetCode === 'string' && facetCode ? facetCode : this.recentCode;

      // Trigger internal save method of last opened facet
      if (facetCode && typeof facetCode === 'object' && !this.openFacetCode) {
        const lastOpenedFacet = this.$refs?.facet?.find((item) => item.facet.code === this.recentCode);

        if (lastOpenedFacet) {
          lastOpenedFacet.saveFacet(false);
        }
      }
    },

    showAll() {
      this.showAllFilters = true;
    },
  },
});
</script>

<style lang="scss" scoped>
.facets {
  position: relative;
  display: flex;

  @include bp(m) {
    margin-top: $pad;
  }

  .facets-header,
  .facets-footer {
    position: relative;
    margin-left: -$grid-margin-width;
    margin-right: -$grid-margin-width;
  }

  .facets-header {
    padding: 0 $grid-margin-width $pad;
    border-bottom: 1px solid palette(sand);
    text-align: center;
    font-weight: bold;
    line-height: 26px;

    .f-close {
      position: absolute;
      top: -12px;
      left: $grid-margin-width;
      flex: 0 1 auto;
      padding: $pad 0;
      color: $color-general-text-brand-default;
    }

    .f-delete {
      position: absolute;
      top: -12px;
      right: $grid-margin-width;
      flex: 0 1 auto;
      padding: $pad 0;
      color: $color-general-text-brand-default;
    }
  }

  .facets-wrapper {
    overflow-x: hidden;
  }

  .facets-footer {
    padding: $pad $grid-margin-width 0;

    .show-results {
      width: 100%;
    }
  }

  &.facets--mobile {
    height: 100%;
    width: 100%;
    flex-direction: column;

    .facets-wrapper {
      flex-shrink: 1;
    }
  }

  &.facets--desktop {
    .facets-wrapper {
      overflow: visible;
      display: flex;
      flex-wrap: wrap;
    }

    .facet {
      margin-right: math.div($grid-margin-width, 2);
      margin-bottom: math.div($grid-margin-width, 2);
    }
  }

  .facet-button {
    border: 1px solid palette(grey, light);
    border-radius: map-get($border-radiuses, 'l');
    background-color: $color-general-background-neutral-default;
    color: palette(black);
    padding: math.div($pad, 3) math.div($pad, 2);
    font-size: map-get($font-sizes, 'm');
  }
}
</style>
